/**
 * WizardShell — full-screen chrome for the marketplace pack composer
 * wizards (Squad / Officials / Venue).
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │  Header: eyebrow, title, close button                          │
 *   ├────────────┬───────────────────────────────────────────────────┤
 *   │            │                                                   │
 *   │  Sidebar   │  Main area (composer picker + per-team cards)     │
 *   │  (sticky)  │  scrolls independently                            │
 *   │            │                                                   │
 *   ├────────────┴───────────────────────────────────────────────────┤
 *   │  Footer: error banner + Cancel + Create                        │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Sidebar holds pack details + a live summary of what's selected.
 * Main area holds whatever the wizard wants to show.
 *
 * Props:
 *   - eyebrow      : small uppercase label (e.g. "Marketplace · SQUAD_PACK")
 *   - title        : main header text
 *   - onClose      : dismiss handler (blocked while `busy`)
 *   - busy         : boolean — disables close + primary action, shows spinner
 *   - error        : string — rendered in a rose banner in the footer
 *   - sidebar      : react node — sticky left column contents
 *   - primaryLabel : string — CTA button label ("Create & publish")
 *   - primaryIcon  : react node — icon shown before the label
 *   - primaryDisabled : boolean
 *   - primaryTone  : "amber" | "sky" | "teal" — button colour
 *   - onPrimary    : click handler for the primary CTA
 *   - secondaryLabel    : string — optional ghost-style CTA to the left of the
 *                         primary button (e.g. "Save draft"). When omitted,
 *                         only the primary button is rendered.
 *   - secondaryIcon     : react node
 *   - secondaryDisabled : boolean
 *   - onSecondary       : click handler for the secondary CTA
 *   - children     : main-area contents
 */
import { useRef } from "react";
import { X, Loader2 } from "lucide-react";
import useDialogDismiss from "../../hooks/useDialogDismiss";

const TONES = {
  amber: "bg-amber-600 hover:bg-amber-500",
  sky:   "bg-sky-600 hover:bg-sky-500",
  teal:  "bg-teal-600 hover:bg-teal-500",
};

export default function WizardShell({
  eyebrow,
  title,
  onClose,
  busy = false,
  error,
  sidebar,
  primaryLabel = "Create & publish",
  primaryIcon = null,
  primaryDisabled = false,
  primaryTone = "amber",
  onPrimary,
  secondaryLabel,
  secondaryIcon = null,
  secondaryDisabled = false,
  onSecondary,
  children,
}) {
  const panelRef = useRef(null);
  const safeClose = busy ? () => {} : (onClose || (() => {}));
  useDialogDismiss({ open: true, onClose: safeClose, ref: panelRef });

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 bg-bg-primary text-text-primary flex flex-col"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-border-default flex items-center justify-between shrink-0">
        <div>
          {eyebrow && (
            <div className="text-xs uppercase tracking-widest text-text-secondary">
              {eyebrow}
            </div>
          )}
          <div className="text-xl font-semibold">{title}</div>
        </div>
        <button
          onClick={onClose}
          disabled={busy}
          className="p-2 rounded hover:bg-bg-secondary disabled:opacity-40"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body — sidebar + main */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <aside className="w-[360px] shrink-0 border-r border-border-default bg-bg-secondary/30 overflow-y-auto">
          <div className="p-6 space-y-4">{sidebar}</div>
        </aside>
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="p-6 max-w-5xl mx-auto space-y-6">{children}</div>
        </main>
      </div>

      {/* Footer */}
      <div className="border-t border-border-default shrink-0">
        {error && (
          <div className="px-6 py-2 bg-rose-500/10 text-rose-200 text-sm border-b border-rose-500/30 whitespace-pre-wrap">
            {String(error)}
          </div>
        )}
        <div className="px-6 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-md border border-border-default hover:bg-bg-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          {secondaryLabel && (
            <button
              onClick={onSecondary}
              disabled={busy || secondaryDisabled}
              className="px-4 py-2 rounded-md border border-border-default hover:bg-bg-secondary text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              {secondaryIcon}{secondaryLabel}
            </button>
          )}
          <button
            onClick={onPrimary}
            disabled={busy || primaryDisabled}
            className={`px-4 py-2 rounded-md ${TONES[primaryTone] || TONES.amber} text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50`}
          >
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
              : <>{primaryIcon}{primaryLabel}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
